import {
  Box,
  Button,
  IconButton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import {
  LandingPage,
  type LandingPagePartner,
  type LandingPagePlansData,
} from '@provision/landing-page-ui';
import type { LandingPageConfig } from '@provision/landing-page-utils';
import { useEffect } from 'react';
import { CopyToClipboardButton, DynamicIcon, Tooltip } from 'src/components';
import { PreviewViewportToggle } from 'src/pages/Settings/memberships/components/preview-viewport-toggle';
import {
  LandingPagePreviewContainer,
  PreviewViewportFrame,
} from 'src/pages/Settings/memberships/styled';
import { buildLandingPagePreviewUrl } from 'src/pages/Settings/memberships/utils/build-landing-page-preview-url';
import {
  clearLandingPagePreview,
  writeLandingPagePreview,
} from 'src/pages/Settings/memberships/utils/landing-page-preview-channel';
import type { PreviewViewport } from 'src/store/memberships-config';
import { useNavigateRoute } from 'src/utils/routing';

type LandingPagePreviewProps = {
  config: LandingPageConfig;
  plans: LandingPagePlansData;
  viewport: PreviewViewport;
  onViewportChange: (viewport: PreviewViewport) => void;
  partner?: LandingPagePartner;
  logoUrl?: string;
  brandId?: string;
  /** The saved page's public address; absent until the page is first saved. */
  liveUrl?: string;
};

const liveFieldSx: SxProps<Theme> = {
  flex: '1 1 16.25rem',
  minWidth: 0,
  height: '2rem',
  px: 1.5,
  borderRadius: 1,
  border: (theme) => `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerLowest,
};

const statusDotSx = (live: boolean): SxProps<Theme> => ({
  width: '0.375rem',
  height: '0.375rem',
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: (theme) =>
    live ? theme.palette.success.main : theme.palette.text.disabled,
});

// Wraps above the actions once the pane is too narrow to show the address beside them.
const LiveLink = ({ url }: { url?: string }) => (
  <Stack direction="row" alignItems="center" gap={1} sx={liveFieldSx}>
    <Box sx={statusDotSx(Boolean(url))} />
    <Typography variant="caption" fontWeight={600} color="text.secondary" flexShrink={0}>
      {url ? 'Live' : 'Not live'}
    </Typography>
    <Typography
      variant="body2"
      color={url ? 'text.primary' : 'text.secondary'}
      noWrap
      flex={1}
      minWidth={0}
    >
      {url ? url.replace(/^https?:\/\//, '') : 'Save the page to publish it'}
    </Typography>
    {url ? (
      <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
        <CopyToClipboardButton
          content={url}
          tooltipText="Copy live page link"
          successMessage="Live page link copied"
        />
        <Tooltip title="Open live page">
          <IconButton
            size="small"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open live page"
            sx={{ p: 0 }}
          >
            <DynamicIcon name="open_in_new" sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      </Stack>
    ) : null}
  </Stack>
);

export const LandingPagePreview = ({
  config,
  plans,
  viewport,
  onViewportChange,
  partner,
  logoUrl,
  brandId,
  liveUrl,
}: LandingPagePreviewProps) => {
  const { partnerPrefix } = useNavigateRoute();
  const previewUrl = buildLandingPagePreviewUrl(partnerPrefix, brandId);

  // Cleared on unmount, which also covers logout and leaving the page, since both unmount this.
  // A brand switch re-runs the effect, so the previous brand's entry goes with it.
  useEffect(() => {
    if (!brandId) return;

    writeLandingPagePreview(brandId, { config, plans, partner, logoUrl });

    return () => {
      clearLandingPagePreview(brandId);
    };
  }, [brandId, config, plans, partner, logoUrl]);

  return (
    <LandingPagePreviewContainer>
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ width: '100%' }}
      >
        <LiveLink url={liveUrl} />
        <Stack direction="row" gap={1} ml="auto">
          <Tooltip title="Opens your unsaved changes in a new tab. Only you can see it.">
            <Button
              size="small"
              variant="outlined"
              startIcon={<DynamicIcon name="visibility" sx={{ fontSize: '1rem' }} />}
              onClick={() => window.open(previewUrl, '_blank')}
            >
              Preview
            </Button>
          </Tooltip>
          <PreviewViewportToggle value={viewport} onChange={onViewportChange} />
        </Stack>
      </Stack>
      <PreviewViewportFrame
        isMobile={viewport === 'mobile'}
        data-testid="preview-viewport-frame"
      >
        <LandingPage config={config} plans={plans} partner={partner} logoUrl={logoUrl} />
      </PreviewViewportFrame>
    </LandingPagePreviewContainer>
  );
};
